import { MapPin, PhoneCall, ShieldAlert } from 'lucide-react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useDistricts, useProvinces } from '../api/referenceApi'
import { FormField } from '../components/FormField'
import type { PersonelFormValues } from '../schemas/personelFormSchema'
import { FormSection } from './FormBits'
import { FULL_ROW } from './formTabs'
import { useFieldError } from './useFieldError'

type PhoneName = 'telefon' | 'is_tel' | 'acil_telefon'

export function IletisimFormTab() {
  const { t } = useTranslation()
  const { register, control, setValue } = useFormContext<PersonelFormValues>()
  const err = useFieldError()
  const ilId = useWatch({ control, name: 'il_id' })
  const provincesQ = useProvinces()
  const districtsQ = useDistricts(ilId)

  const phone = (name: PhoneName, id: string, label: string, required: boolean) => (
    <FormField label={label} htmlFor={id} required={required} error={err(name)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <PhoneInput id={id} className="h-10 rounded-xl" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} aria-invalid={!!err(name)} />
        )}
      />
    </FormField>
  )

  return (
    <div className="space-y-4">
      <FormSection title={t('hr.personel.genel.iletisim')} icon={<PhoneCall />}>
        <FormField label={t('hr.personel.fields.email')} htmlFor="f-email" required error={err('email')} className="sm:col-span-2 lg:col-span-1">
          <Input id="f-email" type="email" className="h-10 rounded-xl" {...register('email')} />
        </FormField>
        {phone('telefon', 'f-tel', t('hr.personel.fields.telefon'), true)}
        {phone('is_tel', 'f-istel', t('hr.personel.fields.isTel'), false)}
      </FormSection>

      <FormSection title={t('hr.personel.form.sections.adres')} icon={<MapPin />} description={t('hr.personel.form.hints.adres')}>
        <FormField label={t('hr.personel.form.il')} required error={err('il_id')}>
          <Controller
            control={control}
            name="il_id"
            render={({ field }) => (
              <Select
                searchable
                size="lg"
                className="rounded-xl"
                value={field.value}
                onChange={(v) => {
                  field.onChange(v)
                  setValue('ilce_id', '', { shouldDirty: true })
                }}
                options={(provincesQ.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
                loading={provincesQ.isLoading}
                placeholder={t('hr.personel.form.selectProvince')}
                searchPlaceholder={t('hr.personel.form.searchProvince')}
                aria-label={t('hr.personel.form.il')}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.form.ilce')} required error={err('ilce_id')}>
          <Controller
            control={control}
            name="ilce_id"
            render={({ field }) => (
              <Select
                searchable
                size="lg"
                className="rounded-xl"
                value={field.value}
                onChange={field.onChange}
                options={(districtsQ.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
                loading={districtsQ.isLoading}
                disabled={!ilId}
                placeholder={t('hr.personel.form.selectDistrict')}
                searchPlaceholder={t('hr.personel.form.searchDistrict')}
                aria-label={t('hr.personel.form.ilce')}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.fields.adres')} htmlFor="f-adres" required error={err('adres')} className={FULL_ROW}>
          <Textarea id="f-adres" className="min-h-20 rounded-xl" {...register('adres')} />
        </FormField>
      </FormSection>

      <FormSection title={t('hr.personel.genel.acil')} icon={<ShieldAlert />}>
        <FormField label={t('hr.personel.fields.ad')} htmlFor="f-acil-ad" required error={err('acil_ad')}>
          <Input id="f-acil-ad" className="h-10 rounded-xl" {...register('acil_ad')} />
        </FormField>
        <FormField label={t('hr.personel.fields.yakinlik')} htmlFor="f-acil-yak" required error={err('acil_yakinlik')}>
          <Input id="f-acil-yak" className="h-10 rounded-xl" {...register('acil_yakinlik')} />
        </FormField>
        {phone('acil_telefon', 'f-acil-tel', t('hr.personel.fields.telefon'), true)}
        <FormField label={t('hr.personel.fields.email')} htmlFor="f-acil-mail" error={err('acil_email')}>
          <Input id="f-acil-mail" type="email" className="h-10 rounded-xl" {...register('acil_email')} />
        </FormField>
        <FormField label={t('hr.personel.fields.adres')} htmlFor="f-acil-adres" error={err('acil_adres')} className="sm:col-span-2">
          <Input id="f-acil-adres" className="h-10 rounded-xl" {...register('acil_adres')} />
        </FormField>
      </FormSection>
    </div>
  )
}
